/**
 * Notice Editor — route component for /dashboard/case-manager/notice-editor.
 *
 * Migrated from the standalone "No-Code Automation" project, whose `src/App.js` was
 * mounted as the whole document by `src/main.js` (`createRoot(...).render(<App />)`).
 * Here it is an ordinary JustAct-CM page rendered by @reach/router inside <MainRoute>,
 * so the mount-time concerns the standalone app could ignore are handled explicitly:
 *
 *   - the two global stylesheets are scoped to the `.nte-root` wrapper below, so they
 *     cannot reach the existing Material-UI screens (see noticeEditorBase.css);
 *   - the `is-dragging-variable` <body> class and the shared portal container are
 *     cleaned up on unmount, because the user can now navigate away from this page.
 *
 * Everything else — editor behaviour, DOCX export, variable handling — is the source
 * logic unchanged.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { VariableSidebar } from './components/VariableSidebar';
import { TemplateEditor } from './components/TemplateEditor';
import { FinalOutputPreview } from './components/FinalOutputPreview';
import { Eye, CloudUpload, Database, X, XCircle, Check, Loader2 } from './components/icons';
import { DEFAULT_PAGE_SETTINGS } from './constants';
import { exportToDocx } from './utils/docxExport';
import { deduplicateTablesInHtml, deduplicateTablesInEditor } from './utils/templateParser';
import { CustomMenuIcon } from './components/CustomMenuIcon.js';
import { useCompactMenu } from '../../../hooks/useCompactMenu';
import { useResizablePanels } from '../../../hooks/useResizablePanels';
import { releaseNoticeEditorPortalRoot } from './utils/portalRoot';
import { ingestDocx, ingestDocxFromUrl } from './utils/docxIngest';
import { ensureNoticeEditorFonts } from './utils/fontLoader';
import './noticeEditorBase.css';
import './noticeEditorLayout.css';

/**
 * @param {object} props
 * @param {boolean} [props.embedded]          render to fill a host container (a dialog)
 *                                            instead of the full dashboard viewport
 * @param {File|Blob|ArrayBuffer} [props.initialDocx]
 *                                            a .docx to load into the editor on mount
 * @param {string} [props.initialDocxUrl]     fallback source if `initialDocx` is absent
 * @param {string} [props.initialTemplateName]
 * @param {(blob: Blob, fileName: string) => Promise<void>|void} [props.onSaveDocx]
 *                                            when given, Save exports the document and
 *                                            hands it back instead of writing localStorage
 * @param {({onConfirm, onCancel}) => void} [props.requestSaveConfirmation]
 *                                            lets the host show its own confirm dialog;
 *                                            without it, Save proceeds immediately
 * @param {() => void} [props.onClose]        called after a successful embedded save, and
 *                                            by the editor's own close control
 */
export default function NoticeEditor({
    embedded = false,
    initialDocx = null,
    initialDocxUrl = '',
    initialTemplateName = '',
    onSaveDocx = null,
    requestSaveConfirmation = null,
    onClose = null,
} = {}) {
    const [editor, setEditor] = useState(null);
    // Embedded-only: surfaces load/export progress and failures inside the host dialog.
    const [embeddedStatus, setEmbeddedStatus] = useState(
        embedded && (initialDocx || initialDocxUrl) ? 'loading' : 'idle',
    );
    const [embeddedError, setEmbeddedError] = useState('');

    const { isCompactView, activeDrawer, setActiveDrawer } = useCompactMenu();

    // Resizable panel widths (Desktop)
    const { dataFieldsWidth, isResizingDataFields, workspaceRef, handleMouseDownDataFields } = useResizablePanels();

    // Always start with a fresh new page / document on page load/refresh
    const [templateName, setTemplateName] = useState(initialTemplateName || 'Untitled Notice');
    const [variables, setVariables] = useState([]);
    const [initialContent] = useState('');
    const [showFinalOutput, setShowFinalOutput] = useState(false);
    const [pageSettings] = useState(DEFAULT_PAGE_SETTINGS);

    // Fonts the source loaded via a CSS @import; see utils/fontLoader.js for why.
    useEffect(() => {
        ensureNoticeEditorFonts();
    }, []);

    // Clear any previous saved document from storage on mount so refresh always gives a new page
    useEffect(() => {
        try {
            localStorage.removeItem('nocode_saved_document');
        } catch (e) {
            console.error('Error clearing saved document on mount:', e);
        }
    }, []);

    // The standalone app lived for the lifetime of the tab; as a JustAct-CM route it can
    // be unmounted by navigation, so drop the global drag class and the shared portal
    // container rather than leaving them behind on <body>.
    useEffect(() => {
        return () => {
            document.body.classList.remove('is-dragging-variable');
            releaseNoticeEditorPortalRoot();
        };
    }, []);

    const saveToLocalStorage = useCallback((editorInstance, name, vars) => {
        if (!editorInstance || editorInstance.isDestroyed) return;
        try {
            const content = editorInstance.getJSON();
            localStorage.setItem('nocode_saved_document', JSON.stringify({
                name: name || 'Untitled Notice',
                content,
                variables: vars || [],
                updatedAt: Date.now()
            }));
        } catch (e) {
            console.error('Error saving to localStorage:', e);
        }
    }, []);

    const handleEditorReady = useCallback((editorInstance) => {
        setEditor(editorInstance);
    }, []);

    // Manual Save Status State: 'idle' | 'saving' | 'saved'
    const [saveStatus, setSaveStatus] = useState('idle');
    const [justSavedManually, setJustSavedManually] = useState(false);

    // Embedded save: export the edited document and give it back to the host (the Notice
    // Generation form re-uploads it to S3 and points the form field at the new file).
    const saveEditedDocxToHost = useCallback(async () => {
        if (!editor || !onSaveDocx) return;
        setSaveStatus('saving');
        setEmbeddedStatus('saving');
        setEmbeddedError('');
        try {
            const blob = await exportToDocx(editor.getJSON(), variables, pageSettings);
            // Strip an existing .docx first, or the sanitiser below swallows the dot and
            // turns "notice.docx" into "noticedocx.docx".
            const safe = (templateName || 'notice-template')
                .replace(/\.docx$/i, '')
                .replace(/[^a-zA-Z0-9_\- ]/g, '')
                .replace(/\s+/g, '_')
                .toLowerCase() || 'notice-template';
            await onSaveDocx(blob, `${safe}.docx`);
            setSaveStatus('saved');
            setEmbeddedStatus('saved');
            if (onClose) onClose();
        } catch (err) {
            console.error('Save failed:', err);
            setSaveStatus('idle');
            setEmbeddedStatus('error');
            setEmbeddedError(err?.message || String(err));
        }
    }, [editor, variables, pageSettings, templateName, onSaveDocx, onClose]);

    const handleSaveToDashboard = useCallback(async () => {
        if (!editor) return;

        // Embedded in the Notice Generation flow: ask the host to confirm first, then
        // write the document back through onSaveDocx instead of touching localStorage.
        if (onSaveDocx) {
            if (requestSaveConfirmation) {
                requestSaveConfirmation({
                    onConfirm: saveEditedDocxToHost,
                    onCancel: () => {},
                });
            } else {
                await saveEditedDocxToHost();
            }
            return;
        }

        setSaveStatus('saving');
        try {
            saveToLocalStorage(editor, templateName, variables);
            setSaveStatus('saved');
            setJustSavedManually(true);
            setTimeout(() => {
                setJustSavedManually(false);
                setSaveStatus('idle');
            }, 2500);
        } catch (err) {
            console.error('Save failed:', err);
            setSaveStatus('idle');
        }
    }, [
        editor,
        templateName,
        variables,
        saveToLocalStorage,
        onSaveDocx,
        requestSaveConfirmation,
        saveEditedDocxToHost,
    ]);

    // ── Ctrl+S / Cmd+S Keyboard Shortcut to Save ──────────────────────────
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
                e.preventDefault();
                e.stopPropagation();
                handleSaveToDashboard();
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [handleSaveToDashboard]);

    const handleInsertVariable = useCallback((variable) => {
        if (!editor) return;
        editor
            .chain()
            .focus()
            .insertContent({
                type: 'variableTag',
                attrs: {
                    variableKey: variable.key || variable.label,
                    key: variable.key || variable.label,
                    label: variable.label,
                },
            })
            .insertContent(' ')
            .run();

        if (isCompactView) {
            setActiveDrawer(null);
        }
    }, [editor, isCompactView, setActiveDrawer]);

    const handleDocxUpload = useCallback((html, extractedVariables) => {
        if (extractedVariables !== undefined) {
            const seenKeys = new Set();
            const docVars = [];
            (extractedVariables || []).forEach(varName => {
                const cleanName = String(varName || '')
                    .replace(/&amp;/g, '&')
                    .replace(/&lt;/g, '<')
                    .replace(/&gt;/g, '>')
                    .replace(/&quot;/g, '"')
                    .replace(/&#39;|&apos;/g, "'")
                    .replace(/<[^>]+>/g, '')
                    .replace(/\*\*/g, '')
                    .replace(/(?:^<<|>>$)/g, '')
                    .trim();
                const key = cleanName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
                if (cleanName && !seenKeys.has(key)) {
                    seenKeys.add(key);
                    docVars.push({
                        key,
                        label: cleanName,
                        category: 'Document Variables',
                        type: 'string',
                        description: 'Document variable'
                    });
                }
            });

            setVariables(prevVars => {
                if (!prevVars || prevVars.length === 0) {
                    return docVars;
                } else {
                    const existingKeys = new Set(prevVars.map(v => (v.key || '').toLowerCase()));
                    const existingLabels = new Set(prevVars.map(v => (v.label || '').toLowerCase().trim()));
                    const newVars = [...prevVars];
                    docVars.forEach(v => {
                        const k = (v.key || '').toLowerCase();
                        const l = (v.label || '').toLowerCase().trim();
                        if (!existingKeys.has(k) && !existingLabels.has(l)) {
                            newVars.push(v);
                        }
                    });
                    return newVars;
                }
            });
        }
        if (editor && html) {
            if (editor.isDestroyed) {
                alert("The editor was reset by an update. Please reload the page to continue.");
                return;
            }
            try {
                if (!editor.commands) {
                    console.error("editor.commands is null or undefined!", editor);
                    alert("Editor commands are not available.");
                    return;
                }
                const cleanHtml = deduplicateTablesInHtml(html);
                editor.commands.setContent(cleanHtml);
                editor.commands.focus();
                deduplicateTablesInEditor(editor);
            }
            catch (err) {
                console.error("Error calling editor.commands.setContent:", err);
                alert("Failed to insert DOCX content into the editor: " + (err.message || String(err)));
            }
        }
    }, [editor]);

    // Embedded: pull in the .docx the host handed us as soon as TipTap is ready. The
    // Notice Generation flow passes the File the user just uploaded (no re-download); the
    // URL is only a fallback for when the File is no longer in memory.
    //
    // The load runs exactly once (`didLoadInitialDocx`), and is only abandoned if the
    // editor unmounts. It deliberately does NOT abandon on a dependency change: TipTap
    // hands a new editor object to onEditorReady as the instance settles, so a
    // per-run cleanup flag would discard the parsed document while the one-shot guard
    // blocked any retry - leaving the overlay up forever with nothing reported.
    // `handleDocxUpload` is read through a ref for the same reason: the version captured
    // when the effect ran may close over a superseded editor.
    const didLoadInitialDocx = useRef(false);
    const isMountedRef = useRef(true);
    const handleDocxUploadRef = useRef(handleDocxUpload);
    handleDocxUploadRef.current = handleDocxUpload;

    useEffect(() => () => { isMountedRef.current = false; }, []);

    useEffect(() => {
        if (!editor || didLoadInitialDocx.current) return;
        if (!initialDocx && !initialDocxUrl) return;

        didLoadInitialDocx.current = true;
        setEmbeddedStatus('loading');
        setEmbeddedError('');

        (async () => {
            try {
                const { html, variableNames } = initialDocx
                    ? await ingestDocx(initialDocx)
                    : await ingestDocxFromUrl(initialDocxUrl);
                if (!isMountedRef.current) return;
                handleDocxUploadRef.current(html, variableNames);
                setEmbeddedStatus('idle');
            } catch (err) {
                if (!isMountedRef.current) return;
                console.error('Could not open the notice template:', err);
                setEmbeddedStatus('error');
                setEmbeddedError(err?.message || String(err));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, initialDocx, initialDocxUrl]);

    const handleVariablesExtractedFromEditor = useCallback((extractedVariables, options = {}) => {
        if (!extractedVariables || extractedVariables.length === 0) return;
        const targetCategory = options?.isManual ? 'Created Variables' : 'Document Variables';

        setVariables(prevVars => {
            const current = prevVars || [];
            const existingKeys = new Set(current.map(v => (v.key || '').toLowerCase()));
            const existingLabels = new Set(current.map(v => (v.label || '').toLowerCase().trim()));

            const toAdd = [];
            extractedVariables.forEach(varName => {
                const cleanName = String(varName || '')
                    .replace(/&amp;/g, '&')
                    .replace(/&lt;/g, '<')
                    .replace(/&gt;/g, '>')
                    .replace(/&quot;/g, '"')
                    .replace(/&#39;|&apos;/g, "'")
                    .replace(/<[^>]+>/g, '')
                    .replace(/\*\*/g, '')
                    .replace(/(?:^<<|>>$)/g, '')
                    .trim();
                if (!cleanName) return;
                const key = cleanName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
                const normLabel = cleanName.toLowerCase();

                if (!existingKeys.has(key) && !existingLabels.has(normLabel)) {
                    existingKeys.add(key);
                    existingLabels.add(normLabel);
                    toAdd.push({
                        key,
                        label: cleanName,
                        category: targetCategory,
                        type: 'string',
                        description: targetCategory === 'Created Variables' ? 'Manually created variable' : 'Document variable'
                    });
                }
            });

            return toAdd.length > 0 ? [...current, ...toAdd] : current;
        });
    }, []);

    return (
      <div
        className={`nte-root${embedded ? ' nte-embedded' : ''}`}
        id="nte-root"
      >
        {/* Embedded only: cover the editor while the host's document is being read or
            written, and surface any failure in place rather than via window.alert. */}
        {embedded && (embeddedStatus === 'loading' || embeddedStatus === 'saving') && (
          <div className="nte-embedded-overlay">
            <Loader2 size={22} className="animate-spin" />
            <span>
              {embeddedStatus === 'loading'
                ? 'Opening the uploaded notice template...'
                : 'Saving the updated notice template...'}
            </span>
          </div>
        )}
        {embedded && embeddedStatus === 'error' && (
          <div className="nte-embedded-error" role="alert">
            <XCircle size={16} />
            <span>{embeddedError || 'Something went wrong.'}</span>
          </div>
        )}
        <div className="app-layout" id="app-layout">
        {/* ── Dashboard Body ────────────────────────────────────────────────── */}
        <main className="app-body" id="app-body">

          {/* Main Content Area */}
          <div className="dashboard-content">
            <div className="content-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '160px' }}>
                {/* Custom Button in Compact Mode to open Data Fields */}
                {isCompactView && (
                  <button
                    type="button"
                    className={`custom-menu-trigger-btn ${activeDrawer === 'datafields' ? 'is-active' : ''}`}
                    onClick={() => setActiveDrawer(activeDrawer === 'datafields' ? null : 'datafields')}
                    title="Toggle Data Fields Sidebar"
                  >
                    <CustomMenuIcon size={22} />
                  </button>
                )}

                <input
                  id="template-name-input"
                  type="text"
                  className="header-template-name"
                  value={templateName}
                  onChange={(e) => {
                    setTemplateName(e.target.value);
                  }}
                  placeholder="Template name..."
                />
              </div>

              <div className="content-header-actions">
                <button
                  type="button"
                  className={`btn-primary ${justSavedManually ? 'btn-saved-success' : ''} ${
                    embedded && (embeddedStatus === 'loading' || embeddedStatus === 'saving')
                      ? 'btn-disabled'
                      : ''
                  }`}
                  onClick={() => handleSaveToDashboard()}
                  disabled={
                    embedded && (embeddedStatus === 'loading' || embeddedStatus === 'saving')
                  }
                  title={
                    embedded
                      ? 'Save and update the uploaded notice template'
                      : 'Save Document'
                  }
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <Loader2 size={16} className="btn-spinner animate-spin" style={{ marginRight: '6px' }}/>
                      Saving...
                    </>
                  ) : justSavedManually ? (
                    <>
                      <Check size={16} style={{ marginRight: '6px' }}/>
                      Saved!
                    </>
                  ) : (
                    <>
                      <CloudUpload size={16} style={{ marginRight: '6px' }}/>
                      Save
                    </>
                  )}
                </button>
                <button type="button" className="btn-primary" onClick={() => setShowFinalOutput(!showFinalOutput)}>
                  <Eye size={16} style={{ marginRight: '6px' }}/>
                  {showFinalOutput ? 'Hide Final Output' : 'Preview Final Output'}
                </button>
              </div>
            </div>

            <div className="editor-workspace" ref={workspaceRef}>
              {/* Desktop Variable Sidebar (Data Fields) */}
              {!isCompactView && (
                <>
                  <div
                    className="variable-sidebar-wrapper"
                    style={{ width: `${dataFieldsWidth}px`, flexShrink: 0, display: 'flex' }}
                  >
                    <VariableSidebar variables={variables} setVariables={setVariables} onDocxUpload={handleDocxUpload} onInsertVariable={handleInsertVariable}/>
                  </div>

                  <div
                    className={`resizer-handle resizer-workspace ${isResizingDataFields ? 'is-resizing' : ''}`}
                    onMouseDown={handleMouseDownDataFields}
                    title="Drag to resize Data Fields / Editor"
                  />
                </>
              )}

              {/* Main Template Editor */}
              <TemplateEditor
                onEditorReady={handleEditorReady}
                variables={variables}
                initialContent={initialContent}
                onVariablesExtracted={handleVariablesExtractedFromEditor}
              />

              {showFinalOutput && editor && (
                <FinalOutputPreview
                  editor={editor}
                  variables={variables}
                  pageSettings={pageSettings}
                  onClose={() => setShowFinalOutput(false)}
                />
              )}
            </div>
          </div>

          {/* Slide-over Drawer for Compact Mode (Data Fields) */}
          {activeDrawer && (
            <div className="drawer-backdrop" onClick={() => setActiveDrawer(null)}>
              <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
                <div className="drawer-header">
                  <div className="drawer-tab-group">
                    <div style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Database size={16} color="#2563eb" />
                      <span>Data Fields</span>
                      <span className="compact-badge">{variables.length}</span>
                    </div>
                  </div>
                  <button type="button" className="drawer-close-btn" onClick={() => setActiveDrawer(null)}>
                    <X size={18} />
                  </button>
                </div>
                <div className="drawer-body">
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <VariableSidebar
                      variables={variables}
                      setVariables={setVariables}
                      onDocxUpload={(html, vars) => {
                        handleDocxUpload(html, vars);
                        setActiveDrawer(null);
                      }}
                      onInsertVariable={handleInsertVariable}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
        </div>
      </div>
    );
}
